import { uniqBy } from 'lodash-es';
import type * as Y from 'yjs';

import {
  type BasicYArray,
  type DisposableElement,
  type WrappedYType,
  isDisposableElement,
  isWrappedYType
} from './types.ts';

const shallowCompare = (obj1: Record<string, any>, obj2: Record<string, any>) =>
  Object.keys(obj1).length === Object.keys(obj2).length &&
  Object.keys(obj1).every(
    (key) =>
      // eslint-disable-next-line no-prototype-builtins
      obj2.hasOwnProperty(key) && obj1[key] === obj2[key]
  );

/**
 * A plain Y.Array, with additional constraints that ensure a given item can only occur
 * at most once.
 */
export class YUniqueArray<T> implements DisposableElement, WrappedYType {
  private readonly array: BasicYArray<T>;

  /** The keys of the unique values in this array, in order. */
  private keys: string[] = [];

  /** Maps the key of each item to the item value. */
  private valuesByKey: Map<string, T> = new Map();

  /**
   * @param array The Y.Array to use as the backing data store.
   * @param getKey Maps each array item to a key. Uniqueness is established based on this key.
   */
  constructor(
    array: BasicYArray<T>,
    private getKey = (val: T) => `${val}`
  ) {
    this.array = array;

    this.initializeValues();
    this.observeChanges();
  }

  public get length(): number {
    return this.keys.length;
  }

  public toArray(): T[] {
    return this.keys.map((k) => this.valuesByKey.get(k)!);
  }

  /**
   * Returns the index of the given value in the array, or undefined if the value is not
   * in the array.
   *
   * @param value The value to find the index of.
   */
  public indexOf(value: T): number | undefined {
    const index = this.keys.indexOf(this.getKey(value));
    return index === -1 ? undefined : index;
  }

  public has(value: T): boolean {
    return this.valuesByKey.has(this.getKey(value));
  }

  public getByKey(key: string): T | undefined {
    return this.valuesByKey.get(key);
  }

  /**
   * Adds the given value to the end of the array if there is no existing occurrence of
   * the value.
   *
   * @param value The value to add.
   */
  public push(value: T): void {
    this.array.doc!.transact(() => {
      const key = this.getKey(value);
      if (!this.valuesByKey.has(key)) {
        this.keys.push(key);
        this.valuesByKey.set(key, value);
        this.array.push([value]);
      }
    });
  }

  /**
   * Adds the given value to the end of the array, removing any existing occurrence of
   * the value.
   *
   * @param value The value to add.
   */
  public addEnd(...values: T[]): void {
    this.onBeforeModification();

    this.array.doc!.transact(() => {
      // Delete first if needed
      this.delete(...values);

      values.forEach((value) => {
        const key = this.getKey(value);
        this.keys.push(key);
        this.valuesByKey.set(key, value);
      });
      this.array.push(values);
    });
  }

  /**
   * Adds the given value to the start of the array, removing any existing occurrence of
   * the value.
   *
   * @param value The value to add.
   */
  public addStart(value: T): void {
    this.onBeforeModification();

    this.array.doc!.transact(() => {
      // Delete first if needed
      if (this.has(value)) {
        this.delete(value);
      }

      const key = this.getKey(value);
      this.keys.unshift(key);
      this.valuesByKey.set(key, value);
      this.array.unshift([value]);
    });
  }

  /**
   * Adds the given value at the given index. If the index is outside of the bounds of
   * the array, the value is added at the end.
   */
  public addAt(value: T, index: number): void {
    this.addBefore([value], this.getByKey(this.keys[index])!);
  }

  /**
   * Adds the given values just before the given item in the array, removing any existing
   * occurrence of the value. If the before value does not exist, inserts at the end of
   * the array.
   *
   * @param values The values to add.
   * @param before The item to insert before.
   */
  public addBefore(values: T[], before: T): void {
    this.onBeforeModification();

    this.array.doc!.transact(() => {
      let index = this.indexOf(before);
      if (index != null) {
        this.array.insert(index, values);
      } else {
        index = this.array.length;
        this.array.push(values);
      }

      // Delete any other instances
      const arr = this.array.toArray();
      for (let i = arr.length - 1; i >= 0; i--) {
        for (let j = 0; j < values.length; j++) {
          const key = this.getKey(values[j]);
          const existingValue = arr[i];
          if (this.getKey(existingValue) === key && i !== index && i !== index + j) {
            this.array.delete(i);
          }
        }
      }

      this.initializeValues();
    });
  }

  /**
   * Adds the given value just after the given item in the array, removing any existing
   * occurrence of the value. If the after value does not exist, inserts at the end of
   * the array.
   *
   * @param value The value to add.
   * @param after The item to insert after.
   */
  public addAfter(value: T, after: T): void {
    this.addAllAfter(after, [value]);
  }

  /**
   * Adds the given value just after the given item in the array, removing any existing
   * occurrence of the value. If the after value does not exist, inserts at the end of
   * the array.
   *
   * If the elements `values` already exist in the same order after `after`
   * then the underlying array is not modified
   *
   * @param values The values to add.
   * @param after The item to insert after.
   */
  public addAllAfter(after: T, values: T[]): void {
    if (values.length === 0) {
      return;
    }

    this.onBeforeModification();

    const uniqueValues = uniqBy(values, (v) => this.getKey(v));

    if (this.checkRangeMatches(this.indexOf(after), uniqueValues)) {
      return;
    }

    this.array.doc!.transact(() => {
      let index = this.indexOf(after);
      if (index != null) {
        index += 1;
        this.array.insert(index, uniqueValues);
      } else {
        index = this.array.length;
        this.array.push(uniqueValues);
      }

      const insertEnd = index + uniqueValues.length;
      // Delete any other instances
      const arr = this.array.toArray();
      const uniqueValueKeys = uniqueValues.map((v) => this.getKey(v));
      for (let i = arr.length - 1; i >= 0; i--) {
        const existingValue = arr[i];

        if (i < insertEnd && i >= index) {
          continue;
        }

        if (uniqueValueKeys.includes(this.getKey(existingValue))) {
          this.array.delete(i);
        }
      }

      this.initializeValues();
    });
  }

  /**
   * Deletes any existing occurrence of the given value.
   *
   * @param value The value to delete.
   */
  public delete(...values: T[]): void {
    this.onBeforeModification();

    const arr = this.array.toArray();
    const keys = values.map((v) => this.getKey(v));

    this.array.doc!.transact(() => {
      for (let i = arr.length - 1; i >= 0; i--) {
        const existingValue = arr[i];
        if (keys.includes(this.getKey(existingValue))) {
          this.array.delete(i);
        }
      }

      this.initializeValues();
    });
  }

  /**
   * Replaces the element at the given position with the new value.
   * If the given position is out of bounds of the array, nothing is done.
   */
  replaceAt(index: number, newValue: T): void {
    if (index < this.keys.length) {
      const val = this.valuesByKey.get(this.keys[index]);
      if (val) {
        this.replace(val, newValue);
      }
    }
  }

  /**
   * Replaces the given element with the given new value.
   * If the given element does not exist in the array, the new value is appended to the end.
   */
  replace(value: T, newValue: T): void {
    this.onBeforeModification();

    this.array.doc!.transact(() => {
      this.delete(newValue);
      const idx = this.indexOf(value);
      if (idx != null) {
        this.delete(value);
        this.addAt(newValue, idx);
      } else {
        this.addEnd(newValue);
      }
    });
  }

  /**
   * Initializes the map containing all elements in the array. Duplicates are removed
   * such that the last duplicate in the array remains.
   */
  private initializeValues() {
    // If we were to construct the map in order of the array, items would end up in the position
    // of their first occurrence. To make them appear in the position of their last occurrence
    // (as the method contract states), we need to construct the map in reverse.
    const reverseMap = new Map(
      this.array
        .toArray()
        .reverse()
        .map((v) => [this.getKey(v), v])
    );
    this.valuesByKey = new Map([...reverseMap].reverse());
    this.keys = [...this.valuesByKey.keys()];
  }

  /**
   * Removes any duplicate items in the array. Duplicates are removed such that the last
   * duplicate in the array remains.
   */
  private removeDuplicates() {
    const arr = this.array.toArray();

    const existingKeys = new Set<string>();
    const duplicatesIndexes: number[] = [];
    for (let i = arr.length - 1; i >= 0; i--) {
      const key = this.getKey(arr[i]);
      if (existingKeys.has(key)) {
        // Value already exists later in the array
        duplicatesIndexes.push(i);
      } else {
        existingKeys.add(key);
      }
    }

    if (duplicatesIndexes.length > 0) {
      this.array.doc!.transact(() => {
        for (const i of duplicatesIndexes) {
          this.array.delete(i);
        }
      });
    }
  }

  /**
   * Compare a range of value to check if they match
   * @param startExclusive - the starting index, a subarray starting after this index is compared
   * @param range The range to compare
   * @private
   *
   * @returns true if the ranges match, false otherwise
   */
  private checkRangeMatches(startExclusive: number | undefined, range: T[]): boolean {
    if (startExclusive === undefined) {
      return false;
    }

    const arr = this.keys.slice(startExclusive + 1, startExclusive + range.length + 1);

    return shallowCompare(
      range.map((v) => this.getKey(v)),
      arr
    );
  }

  /**
   * Begins to observe the underlying Y.Array for changes, emitting 'change' events on
   * the YUniqueArray.
   */
  private observeChanges() {
    this.array.observeDeep(this.onChange);
  }

  private onBeforeModification() {
    this.removeDuplicates();
  }

  private onChange = () => {
    this.initializeValues();
  };

  unwrap(): Y.AbstractType<any> | undefined {
    return isWrappedYType(this.array)
      ? this.array.unwrap()
      : (this.array as Y.Array<unknown>);
  }

  dispose(): void {
    this.array.unobserveDeep(this.onChange);

    if (isDisposableElement(this.array)) {
      this.array.dispose();
    }
  }
}
